import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useApiClient } from "@/api/client"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import type { Milestone } from "./useMilestones"

export type CreateMilestoneInput = {
  name: string
  description?: string
  date?: string
  plannedDate?: string
  done?: boolean
  projectId: string
}

export type UpdateMilestoneInput = {
  name?: string
  description?: string | null
  date?: string | null
  plannedDate?: string | null
  done?: boolean
  projectId?: string
}

export function useMilestoneMutations() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug
  const queryClient = useQueryClient()

  const createMilestone = useMutation({
    mutationFn: async (input: CreateMilestoneInput) => {
      const body: Record<string, unknown> = {
        name: input.name,
        project_id: input.projectId,
      }
      if (input.description) body.description = input.description
      if (input.date) body.date = input.date
      if (input.plannedDate) body.planned_date = input.plannedDate
      if (input.done !== undefined) body.done = input.done
      return fetchWithAuth<Milestone>(`/api/v1/workspaces/${slug}/milestones`, {
        method: "POST",
        body: JSON.stringify(body),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", slug] })
    },
  })

  const updateMilestone = useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string
      patch: UpdateMilestoneInput
    }) => {
      const body: Record<string, unknown> = {}
      if (patch.name !== undefined) body.name = patch.name
      if (patch.description !== undefined) body.description = patch.description
      if (patch.date !== undefined) body.date = patch.date
      if (patch.plannedDate !== undefined) body.planned_date = patch.plannedDate
      if (patch.done !== undefined) body.done = patch.done
      if (patch.projectId !== undefined) body.project_id = patch.projectId
      return fetchWithAuth<Milestone>(
        `/api/v1/workspaces/${slug}/milestones/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", slug] })
    },
  })

  const archiveMilestone = useMutation({
    mutationFn: async (id: string) => {
      return fetchWithAuth<{ status: string }>(
        `/api/v1/workspaces/${slug}/milestones/${id}/archive`,
        { method: "POST" },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones", slug] })
    },
  })

  return { createMilestone, updateMilestone, archiveMilestone }
}
